import classNames from "classnames";
import {
    useCallback,
    useMemo,
    Fragment,
    useState,
    useRef,
    useEffect,
} from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    Segment,
    Subsegment as SubsegmentType,
    calculateWidthPercentage,
    getSubsegmentStyle,
} from "@helpers/segment";
import { ClickableContainer } from "@design-system/ClickableContainer/ClickableContainer";
import { SegmentData } from "./SegmentData";
import styles from "./SegmentBar.module.scss";
import { redistributeWidths, SegmentWithSpacing } from "./utils";

const SEGMENT_MIN_WIDTH = 2;

interface Props {
    className?: string;
    segments: Segment[];
    formatValueAsMoney?: boolean;
    hasMinimumWidth?: boolean;
    showSubsegmentsOnHover?: boolean;
    onSegmentClick?: (segmentId: string) => void;
}

interface SubsegmentProps {
    segment: Segment;
    subsegment: SubsegmentType;
    index: number;
    formatValueAsMoney?: boolean;
    onClick?: (subsegmentId: string) => void;
    hideTooltips?: boolean;
}

const calculateGapWidth = (
    totalValue: number,
    currentValue: number,
    nextValue: number,
) => {
    if (totalValue === 0) {
        return "2px";
    }
    if (currentValue === 0 || nextValue === 0) {
        return "none";
    }
    return "2px";
};

const Subsegment: React.FC<SubsegmentProps> = ({
    segment,
    subsegment,
    index,
    formatValueAsMoney,
    onClick,
    hideTooltips,
}) => {
    const showGap =
        index < (segment.subsegments?.length ?? 0) - 1 &&
        subsegment.value > 0 &&
        segment.subsegments?.slice(index + 1).some(({ value }) => value > 0);

    return (
        <Fragment>
            <Tooltip content={subsegment.tooltip} disabled={hideTooltips}>
                <ClickableContainer
                    className={styles.subsegment}
                    style={getSubsegmentStyle(segment, subsegment, index)}
                    onClick={() => {
                        onClick?.(subsegment.id);
                    }}
                >
                    <SegmentData
                        label={subsegment.label}
                        value={subsegment.value}
                        valueOverride={subsegment.valueOverride}
                        subtitle={subsegment.subtitle}
                        subtitleColor={segment.colors.subtitle}
                        formatValueAsMoney={formatValueAsMoney}
                        className={styles.subsegmentData}
                    />
                </ClickableContainer>
            </Tooltip>
            {showGap ? (
                <div
                    className={styles.subsegmentGap}
                    style={{
                        width: calculateGapWidth(
                            segment.value,
                            subsegment.value,
                            segment.subsegments?.[index + 1]?.value ?? 0,
                        ),
                    }}
                />
            ) : null}
        </Fragment>
    );
};

export const SegmentBar: React.FC<Props> = ({
    className,
    segments,
    formatValueAsMoney,
    onSegmentClick,
    hasMinimumWidth,
    showSubsegmentsOnHover,
}) => {
    const [hoveredSegmentId, setHoveredSegmentId] = useState<
        string | undefined
    >(undefined);
    const [hideTooltips, setHideTooltips] = useState(false);
    const segmentBarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const bar = segmentBarRef.current;
        if (!bar) {
            return;
        }
        const handleTransitionEvent = (e: TransitionEvent) => {
            if (e.propertyName === "width") {
                if (e.type === "transitionstart") {
                    setHideTooltips(true);
                } else if (e.type === "transitionend") {
                    setHideTooltips(false);
                }
            }
        };
        bar.addEventListener("transitionstart", handleTransitionEvent);
        bar.addEventListener("transitionend", handleTransitionEvent);
        return () => {
            bar.removeEventListener("transitionstart", handleTransitionEvent);
            bar.removeEventListener("transitionend", handleTransitionEvent);
        };
    }, [segments]);

    const showSubsegments = useCallback(
        (segment: SegmentWithSpacing) => {
            if (segment.value === 0) {
                return false;
            }

            if (showSubsegmentsOnHover && hoveredSegmentId === segment.id) {
                return true;
            } else if (showSubsegmentsOnHover) {
                return false;
            }

            const total = segments.reduce((acc, s) => acc + s.value, 0);
            return segment.value === total;
        },
        [showSubsegmentsOnHover, hoveredSegmentId, segments],
    );

    const isLastNonZeroSegment = useCallback(
        (index: number) => {
            const remainingSegments = segments.slice(index + 1);
            return remainingSegments.every((segment) => segment.value === 0);
        },
        [segments],
    );

    const totalValue = useMemo(
        () => segments.reduce((acc, s) => acc + s.value, 0),
        [segments],
    );

    const segmentsWithSpacing: SegmentWithSpacing[] = useMemo(() => {
        const result = segments.map((segment, index) => {
            const segmentWidthPercentage =
                totalValue === 0 && segments.length > 0
                    ? 100 / segments.length
                    : calculateWidthPercentage(segment, segments);

            const showGap =
                index < segments.length - 1 &&
                segment.value > 0 &&
                (totalValue === 0 || !isLastNonZeroSegment(index));

            const gapWidth = calculateGapWidth(
                totalValue,
                segment.value,
                segments[index + 1]?.value,
            );

            return {
                ...segment,
                width: segmentWidthPercentage,
                gapWidth,
                showGap,
            };
        });

        if (hasMinimumWidth) {
            return redistributeWidths(result, SEGMENT_MIN_WIDTH);
        }

        return result;
    }, [isLastNonZeroSegment, hasMinimumWidth, segments, totalValue]);

    return (
        <div
            ref={segmentBarRef}
            className={classNames(styles.segmentBar, className)}
        >
            {segmentsWithSpacing.map((segment) => (
                <Fragment key={segment.id}>
                    <Tooltip content={segment.tooltip}>
                        <ClickableContainer
                            onClick={() => {
                                if (
                                    !showSubsegments(segment) ||
                                    showSubsegmentsOnHover
                                ) {
                                    onSegmentClick?.(segment.id);
                                }
                            }}
                            className={classNames(
                                styles.segment,
                                {
                                    [styles.withSubsegments]:
                                        showSubsegments(segment),
                                    [styles.showSubsegmentsOnHover]:
                                        showSubsegmentsOnHover,
                                    withSubsegments: showSubsegments(segment),
                                },
                                "segment",
                                `segment-${segment.id}`,
                            )}
                            style={{
                                width: `${segment.width}%`,
                                backgroundColor: segment.colors.start,
                            }}
                            onMouseEnter={() => setHoveredSegmentId(segment.id)}
                            onMouseLeave={() => setHoveredSegmentId(undefined)}
                        >
                            <SegmentData
                                label={segment.label}
                                value={segment.value}
                                valueOverride={segment.valueOverride}
                                subtitle={segment.subtitle}
                                formatValueAsMoney={formatValueAsMoney}
                                subtitleColor={segment.colors.subtitle}
                            />
                            <div className={styles.subsegments}>
                                {segment.subsegments?.map(
                                    (subsegment, subIndex) => (
                                        <Subsegment
                                            key={subsegment.id}
                                            segment={segment}
                                            subsegment={subsegment}
                                            index={subIndex}
                                            formatValueAsMoney={
                                                formatValueAsMoney
                                            }
                                            onClick={onSegmentClick}
                                            hideTooltips={hideTooltips}
                                        />
                                    ),
                                )}
                            </div>
                        </ClickableContainer>
                    </Tooltip>

                    {segment.showGap && (
                        <div
                            className={styles.segmentGap}
                            style={{ width: segment.gapWidth }}
                        />
                    )}
                </Fragment>
            ))}
        </div>
    );
};
